import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, Calendar, Pin } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/layout/ScrollReveal';
import { getPosts } from '../lib/api';

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Journal';
    getPosts()
      .then((res) => setPosts(res.data.posts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-fog" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <div className="flex items-center gap-2 mb-5">
              <Flame className="h-4 w-4 text-[#E8722A]" />
              <span className="kicker">Journal</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">
              Dispatches from<br />the engine room
            </h1>
            <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-xl">
              Insights, updates, and perspectives from the Visualte infrastructure team.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 relative" data-testid="blog-grid-section">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="hud-card border border-white/10 bg-white/[0.03] p-6">
                  <Skeleton className="h-32 w-full mb-4 rounded-lg bg-white/10" />
                  <Skeleton className="h-5 w-3/4 mb-2 bg-white/10" />
                  <Skeleton className="h-4 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <Flame className="h-8 w-8 text-[#E8722A]/30 mx-auto mb-4" />
              <p className="text-white/40 text-sm">No journal entries yet. Check back soon.</p>
            </div>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {posts.map((post) => (
                <StaggerItem key={post.id}>
                  <Link to={`/journal/${post.slug}`} data-testid="blog-post-card">
                    <div className={`hud-card border ${post.pinned ? 'border-[#E8722A]/25 bg-[#E8722A]/[0.04]' : 'border-white/10 bg-white/[0.03]'} overflow-hidden h-full card-hover-glow group`}>
                      {post.coverImageUrl && (
                        <div className="h-40 overflow-hidden">
                          <img src={post.coverImageUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.pinned && <Badge className="bg-[#E8722A]/15 text-[#E8722A] border-[#E8722A]/25 text-[10px] gap-0.5"><Pin className="h-2.5 w-2.5" />Pinned</Badge>}
                          {(post.tags || []).slice(0, 2).map((tag) => (
                            <Badge key={tag} className="bg-[rgba(232,114,42,0.10)] text-[rgba(255,180,80,0.85)] border-[rgba(232,114,42,0.18)] text-[10px]">{tag}</Badge>
                          ))}
                        </div>
                        <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2 group-hover:text-[#E8722A] transition-colors duration-200 line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-sm text-white/55 leading-relaxed line-clamp-3 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-white/40">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(post.displayDate || post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <span className="text-[#E8722A] flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </div>
      </section>
    </PageTransition>
  );
}
